import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Dashboard, ReportsOverview } from 'shared';
import { api } from '@/lib/api';

export type { Dashboard, ReportsOverview };

/** The slice of data a report covers. */
export interface ReportFilter {
  /** "YYYY-MM-DD", both days included. */
  from: string;
  to: string;
  /** Zero for every centre. */
  centerId: number;
}

export interface ParticipantStats {
  total: number;
  active: number;
  pending: number;
  inactive: number;
  pendingConsents: number;
  newThisMonth: number;
  newLastMonth: number;
}

export const reportKeys = {
  all: ['reports'] as const,
  overview: (filter: ReportFilter) => [...reportKeys.all, 'overview', filter] as const,
  dashboard: (centerId: number) => [...reportKeys.all, 'dashboard', centerId] as const,
  participants: () => [...reportKeys.all, 'participants'] as const,
};

/** How often the dashboard is read again: it shows what is happening now. */
export const DASHBOARD_REFRESH_MS = 30_000;

/**
 * The report of a period. While another period loads the one on screen stays,
 * so the page does not collapse and jump between two filters.
 */
export function useReportsOverview(filter: ReportFilter, enabled = true) {
  const params = new URLSearchParams({ from: filter.from, to: filter.to });
  if (filter.centerId > 0) params.set('digitalCenterId', String(filter.centerId));

  return useQuery({
    queryKey: reportKeys.overview(filter),
    queryFn: () => api.get<ReportsOverview>(`/api/reports/overview?${params}`),
    enabled,
    staleTime: 60_000,
    placeholderData: (previous) => previous,
  });
}

/** What is happening today in a centre, or in every centre for zero. */
export function useDashboard(centerId: number) {
  const query = centerId > 0 ? `?digitalCenterId=${centerId}` : '';
  return useQuery({
    queryKey: reportKeys.dashboard(centerId),
    queryFn: () => api.get<Dashboard>(`/api/reports/dashboard${query}`),
    refetchInterval: DASHBOARD_REFRESH_MS,
    placeholderData: (previous) => previous,
  });
}

/** How many participants are registered, and in what state. */
export function useParticipantStats() {
  return useQuery({
    queryKey: reportKeys.participants(),
    queryFn: () => api.get<ParticipantStats>('/api/participants/stats'),
    staleTime: 60_000,
  });
}

/** End a participant's equipment session for them. */
export function useEndEquipmentSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: number) =>
      api.post<void>(`/api/participant-equipment-assignments/${sessionId}/end`, {}),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: reportKeys.all });
      qc.invalidateQueries({ queryKey: ['equipments'] });
      qc.invalidateQueries({ queryKey: ['presences'] });
    },
  });
}
